import {useCallback} from 'react';
import {useAnalyzeAudienceCsv, useImportAudienceLeads} from '@tinct/core-api';
import {
  AnalyzeCsvResponse,
  AudienceImportContextRef,
  CrmSegment,
  CsvMapping,
  ImportResponse,
  ReadCompanyRef,
} from '@tinct/core-api-models';

// Utility to convert file to base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
  });
};

export type AnalyzeCsvParams = {
  file: File;
  audienceContext: AudienceImportContextRef;
};

export type ImportCsvParams = {
  audienceContext: AudienceImportContextRef;
  fileBase64: string;
  mapping: CsvMapping;
};

export type ImportCompaniesParams = {
  audienceContext: AudienceImportContextRef;
  companies: ReadCompanyRef[];
};

export type ImportCrmSegmentParams = {
  audienceContext: AudienceImportContextRef;
  integrationId: string;
  segment: CrmSegment;
};

export type UseCreateAudienceActionsReturn = {
  analyzeAction: {
    analyzeCsv: (
      params: AnalyzeCsvParams,
    ) => Promise<{ analysis: AnalyzeCsvResponse; base64: string } | undefined>;
    isAnalyzing: boolean;
  };
  importAction: {
    importCsv: (params: ImportCsvParams) => Promise<ImportResponse | undefined>;
    importCompanies: (params: ImportCompaniesParams) => Promise<ImportResponse | undefined>;
    importCrmSegment: (params: ImportCrmSegmentParams) => Promise<ImportResponse | undefined>;
    isImporting: boolean;
  };
};

export const useCreateAudienceActions = (): UseCreateAudienceActionsReturn => {
  const { mutateAsync: analyzeCsvMutation, isPending: isAnalyzing } =
    useAnalyzeAudienceCsv();
  const { mutateAsync: importLeadsMutation, isPending: isImporting } =
    useImportAudienceLeads();

  const analyzeCsv = useCallback(
    async (params: AnalyzeCsvParams) => {
      try {
        const base64 = await fileToBase64(params.file);
        const analysis = await analyzeCsvMutation({
          csv: { base64 },
          context: { audience: params.audienceContext },
        });
        return { analysis, base64 };
      } catch {
        return undefined;
      }
    },
    [analyzeCsvMutation],
  );

  const importCsv = useCallback(
    async (params: ImportCsvParams) => {
      try {
        return await importLeadsMutation({
          type: 'csv',
          context: { audience: params.audienceContext },
          csv: { base64: params.fileBase64 },
          mapping: params.mapping,
        });
      } catch {
        return undefined;
      }
    },
    [importLeadsMutation],
  );

  const importCompanies = useCallback(
    async (params: ImportCompaniesParams) => {
      try {
        return await importLeadsMutation({
          type: 'simple_list',
          context: { audience: params.audienceContext },
          list: params.companies.map((company) => ({
            company: { id: company.id },
          })),
        });
      } catch {
        return undefined;
      }
    },
    [importLeadsMutation],
  );

  const importCrmSegment = useCallback(
    async (params: ImportCrmSegmentParams) => {
      try {
        return await importLeadsMutation({
          type: 'crm_integration',
          context: { audience: params.audienceContext },
          crmIntegration: { id: params.integrationId },
          crmSegment: { id: params.segment.id! },
        });
      } catch {
        return undefined;
      }
    },
    [importLeadsMutation],
  );

  return {
    analyzeAction: {
      analyzeCsv,
      isAnalyzing,
    },
    importAction: {
      importCsv,
      importCompanies,
      importCrmSegment,
      isImporting,
    },
  };
};
